import { DefaultTFuncReturn } from 'i18next';
import { DateTime } from 'luxon';
import { MinMaxPrices } from '../types/MinMaxPricesState';
import { formatMoney } from './formatMoney';
import { buildPriceToShow, getTaxPrice } from './tickets';
import { ITaxRate } from 'app/pages/TicketSalePage/slice/types';
import { EventType } from 'types/EventState';
import {
  EVENT_TYPE_FREE,
  EVENT_TYPE_PAID,
  EVENT_TYPE_WITH_CALENDAR,
  EVENT_TYPE_WITH_SEATING_CHART,
} from './static/constants';

const formatTimeOnTimeZone = (date, time_format, timezone) => {
  const format = getMatchingTimeFormat(time_format);

  return DateTime.fromISO(date).setZone(timezone).toFormat(format);
};

const getMatchingTimeFormat = format => {
  switch (format) {
    case 'H:i':
      return 'HH:mm';
    case 'h:i A':
      return 'hh:mma';
    default:
      return 'HH:mm';
  }
};

export const formatDateOnTimeZone = (
  date,
  dateFormat,
  timezone,
  withoutYear = false,
) => {
  const format = getMatchingDateFormat(dateFormat, withoutYear);

  return DateTime.fromISO(date).setZone(timezone).toFormat(format);
};

const getMatchingDateFormat = (format, withoutYear = false) => {
  switch (format) {
    case 'M d, Y':
      return withoutYear ? 'LLL dd' : 'LLL dd, yyyy';
    case 'd M, Y':
      return withoutYear ? 'dd LLL' : 'dd LLL, yyyy';
    case 'd.m.Y':
      return withoutYear ? 'dd.LL.yyyy' : 'dd.LL.yyyy';
    case 'm/d/Y':
      return withoutYear ? 'LL/dd/yyyy' : 'LL/dd/yyyy';
    default:
      return withoutYear ? 'LLL dd' : 'LLL dd, yyyy';
  }
};

export const formatDateAndTimeOnTimeZone = (
  date: string,
  dateFormat: string,
  timeFormat: string,
  timezone: string,
  connectingWord: string,
  withoutYear?: boolean,
): any => {
  const startTime = formatTimeOnTimeZone(date, timeFormat, timezone);
  const startDate = formatDateOnTimeZone(
    date,
    dateFormat,
    timezone,
    withoutYear,
  );

  return `${startDate} ${connectingWord} ${startTime}`;
};

export const formatDateAndTimeIntervalOnTimeZone = (
  timezone: string,
  dateFormat: string,
  timeFormat: string,
  startsAt: string,
  endsAt: string,
  connectingWord: string,
): any => {
  const startTime = formatTimeOnTimeZone(startsAt, timeFormat, timezone);
  const endTime = formatTimeOnTimeZone(endsAt, timeFormat, timezone);

  const startDate = formatDateOnTimeZone(startsAt, dateFormat, timezone);
  const endDate = formatDateOnTimeZone(endsAt, dateFormat, timezone);

  if (startDate === endDate) {
    return `${startDate}, ${startTime} - ${endTime}`;
  }

  return `${startDate} ${connectingWord} ${startTime} - ${endDate} ${connectingWord} ${endTime}`;
};

export const formatDueDate = (
  dateFormat: string,
  timeFormat: string,
  placedAt: string,
  createdAt: string,
  dueDays: number,
  connectingWord: string,
): string => {
  let dueDate = DateTime.fromISO(placedAt || createdAt);
  let remainingDays = dueDays;

  while (remainingDays > 0) {
    dueDate = dueDate.plus({ days: 1 });

    if (!dueDate.isWeekend) {
      remainingDays--;
    }
  }

  return dueDate.toFormat(
    `${getMatchingDateFormat(
      dateFormat,
    )} '${connectingWord}' ${getMatchingTimeFormat(timeFormat)}`,
  );
};

export const getDateInTimeZone = (date: string, timezone: string) =>
  DateTime.fromISO(date).setZone(timezone);

export const minMaxRangePrice = (
  prices: MinMaxPrices | undefined,
  currency: string,
  tax_included: boolean,
  tax_rate: ITaxRate,
  freeText?: string | DefaultTFuncReturn,
): string | 0 => {
  if (prices?.minPrice || prices?.maxPrice) {
    let minPrice = prices?.minPrice;
    let maxPrice = prices?.maxPrice;
    if (tax_included) {
      minPrice += getTaxPrice(minPrice, tax_rate.rate);
      maxPrice += getTaxPrice(maxPrice, tax_rate.rate);
    }
    if (prices?.minPrice !== prices?.maxPrice) {
      return [
        buildPriceToShow(minPrice, currency, freeText),
        buildPriceToShow(maxPrice, currency, freeText),
      ].join(' - ');
    } else {
      return buildPriceToShow(minPrice, currency, freeText);
    }
  }

  return freeText || 0;
};

export const getStartingPrice = (
  prices: MinMaxPrices | null,
  currency: string,
  tax_included: boolean,
  tax_rate: ITaxRate,
) => {
  if (!prices) return;

  let result = prices.minPrice;

  if (tax_included) {
    result += getTaxPrice(result, tax_rate.rate);
  }

  return formatMoney(result, currency);
};

export const getEventSlug = (eventName: string, eventId: string) =>
  eventName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, '-') +
  '-' +
  eventId;

export const getEventIdFromSlug = (eventSlug: string | undefined) =>
  eventSlug ? eventSlug.split('-')[eventSlug.split('-').length - 1] : '';

export const getEventDetailsLink = (eventName: string, eventId: string) =>
  `/event/${getEventSlug(eventName, eventId)}`;

export const getTicketsPageLink = (
  eventName: string,
  eventId: string,
  eventType: EventType,
): string => {
  const prefix = getEventDetailsLink(eventName, eventId);

  switch (eventType) {
    case EVENT_TYPE_PAID:
      return `${prefix}/buy-tickets`;
    case EVENT_TYPE_FREE:
      return `${prefix}/registration`;
    case EVENT_TYPE_WITH_SEATING_CHART:
      return `${prefix}/reserve-seats`;
    case EVENT_TYPE_WITH_CALENDAR:
      return `${prefix}/calendar`;
  }
};
